/**
 * formatAnswer
 * ------------
 * Converts the raw value from `formData` into a human-readable string.
 *
 * • Handles translations for option labels (`labelKey`) and question titles
 *   via the supplied `t` function (optional).
 * • Falls back gracefully when a translation or label is missing.
 *
 * @param {object}  question  – Question definition (may contain `titleKey`,
 *                              `descriptionKey`, option `labelKey`, etc.).
 * @param {object}  formData  – Answers keyed by question.id.
 * @param {Function} [t]      – i18next translate fn (pass from the caller).
 * @returns {string}            Localised, human-friendly string.
 */
// utils/form/formatAnswer.js
import { questionUsesFreeText, selectionIncludesValue } from "./formSelection";

export default function formatAnswer(question, formData, t = (k) => k) {
  const raw = formData?.[question.id];

  if (raw == null || raw === "") return t("common.na") ?? "N/A";

  switch (question.fieldType) {
    case "date": {
      const d = new Date(raw);
      return Number.isNaN(d.getTime()) ? String(raw) : d.toLocaleDateString();
    }

    case "radio":
    case "select": {
      const opt = question.options?.find(
        (o) => String(o.value) === String(raw)
      );
      const label = opt
        ? opt.labelKey
          ? t(opt.labelKey)
          : opt.label ?? String(raw)
        : String(raw);

      if (
        question.freeTextFieldId &&
        String(raw) === String(question.freeTextTriggerValue ?? "Other")
      ) {
        const otherText = formData?.[question.freeTextFieldId];

        if (otherText != null && String(otherText).trim() !== "") {
          return `${label}: ${String(otherText).trim()}`;
        }
      }

      return label;
    }

    case "checkbox-group": {
      const selectedValues = Array.isArray(raw) ? raw : [];

      if (selectedValues.length === 0) {
        return t("common.na") ?? "N/A";
      }

      const otherText = formData?.[question.freeTextFieldId];

      const labels = selectedValues.map((selectedValue) => {
        const opt = question.options?.find((option) =>
          selectionIncludesValue(selectedValue, option.value),
        );

        const label = opt
          ? opt.labelKey
            ? t(opt.labelKey)
            : opt.label ?? String(selectedValue)
          : String(selectedValue);

        if (
          questionUsesFreeText(question, raw) &&
          selectionIncludesValue(
            selectedValue,
            question.freeTextTriggerValue ?? "Other",
          ) &&
          otherText != null &&
          String(otherText).trim() !== ""
        ) {
          return `${label}: ${String(otherText).trim()}`;
        }

        return label;
      });

      return labels.join(", ");
    }

    case "episode-timing": {
      const timingFields =
        question.timingFields && typeof question.timingFields === "object"
          ? question.timingFields
          : {};
      const timingValue = raw && typeof raw === "object" ? raw : null;

      if (!timingValue) {
        return t("common.na") ?? "N/A";
      }

      if (timingValue.unknown === true) {
        return t(
          timingFields.unknownLabelKey ??
            timingFields.unknownLabel ??
            "I can't remember these details",
        );
      }

      const onsetDate = timingValue.approximateOnsetDate
        ? new Date(timingValue.approximateOnsetDate)
        : null;
      const ongoingOption = Array.isArray(timingFields.ongoingOptions)
        ? timingFields.ongoingOptions.find(
            (option) => option.value === timingValue.ongoing,
          )
        : null;

      return [
        `${t(
          timingFields.onsetDateLabelKey ??
            timingFields.onsetDateLabel ??
            "Approximate start date",
        )}: ${
          onsetDate && !Number.isNaN(onsetDate.getTime())
            ? onsetDate.toLocaleDateString()
            : timingValue.approximateOnsetDate || (t("common.na") ?? "N/A")
        }`,
        `${t(
          timingFields.durationDaysLabelKey ??
            timingFields.durationDaysLabel ??
            "Duration in days",
        )}: ${String(timingValue.durationDays ?? t("common.na") ?? "N/A")}`,
        `${t(
          timingFields.ongoingLabelKey ??
            timingFields.ongoingLabel ??
            "Is it still ongoing?",
        )}: ${
          ongoingOption
            ? t(ongoingOption.labelKey ?? ongoingOption.label)
            : timingValue.ongoing === true
            ? "Yes"
            : timingValue.ongoing === false
            ? "No"
            : (t("common.na") ?? "N/A")
        }`,
      ].join("\n");
    }

    case "boolean-matrix": {
      const yes = t("sgrq.scales.yesNo.true") || "True";
      const no = t("sgrq.scales.yesNo.false") || "False";
      const rows = question.rows || [];
      const lines = rows.map((r) => {
        const v = raw?.[r.id];
        const txt =
          v === true ? yes : v === false ? no : t("common.na") ?? "N/A";
        const label = t(r.labelKey ?? r.label ?? r.id);
        return `• ${label}: ${txt}`;
      });
      return lines.join("\n"); // always a string
    }

    case "range":
    case "number":
    case "textarea":
    case "text":
      return String(raw);

    default:
      return typeof raw === "object" ? JSON.stringify(raw) : String(raw);
  }
}
